import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from ".";

const safeJson = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

const pickPreviewImage = (data) => {
  if (!data) return null;
  if (typeof data === "string") return data;

  // Common backend keys (keep this tolerant; the API has evolved in the past)
  const candidates = [
    data.image,
    data.preview_image,
    data.previewImage,
    data.preview,
    data.share_image,
    data.shareImage,
    data.generated_image,
    data.generatedImage,
    data.result_image,
    data.resultImage,
  ];

  for (const v of candidates) {
    if (typeof v === "string" && v.trim()) return v;
  }

  // Sometimes nested under "data"
  const nested = data?.data;
  if (nested && typeof nested === "object") {
    return pickPreviewImage(nested);
  }

  return null;
};

const pickYmd = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v.trim())
    ? v.trim()
    : null;

/**
 * POST /api/master-club-measure-diary/can-share/{start_date}/{end_date}
 *
 * The backend generates the preview image regardless of can_share.
 * This thunk stores both `can_share` and the generated `image` URL.
 *
 * When the request opts into `resolve_nearest`, the backend answers for the
 * nearest dates the user actually recorded rather than dead-ending on a date
 * with no measurement. It reports those back, so the answer here always carries
 * the dates it really refers to — the editor re-labels its pills from these.
 */
export const checkPrimaDopoCanShare = createAsyncThunk(
  "primaDopo/checkCanShare",
  async (
    { startDate, endDate, payload = {} },
    { rejectWithValue },
  ) => {
    try {
      if (!startDate || !endDate) {
        throw new Error("Missing start/end dates");
      }

      const res = await fetch(
        `${baseUrl}/api/master-club-measure-diary/can-share/${startDate}/${endDate}`,
        {
          method: "POST",
          headers: headersDefault(),
          body: JSON.stringify(payload || {}),
        },
      );

      const data = await safeJson(res);
      if (!res.ok) {
        // Surface the real backend message + HTTP status instead of a generic
        // string, so a server-side image-generation failure is diagnosable
        // rather than swallowed by the UI's fallback toast.
        const detail =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          null;
        throw new Error(
          detail
            ? `${detail} (HTTP ${res.status})`
            : `Errore generazione anteprima (HTTP ${res.status})`,
        );
      }

      // Fall back to the requested dates when the backend doesn't report
      // resolved ones (older builds, or a request that didn't opt in), so
      // consumers can always read these two fields.
      const resolvedStartDate = pickYmd(data?.resolved_start_date) || startDate;
      const resolvedEndDate = pickYmd(data?.resolved_end_date) || endDate;

      return {
        startDate,
        endDate,
        resolvedStartDate,
        resolvedEndDate,
        datesAdjusted:
          !!data?.dates_adjusted &&
          (resolvedStartDate !== startDate || resolvedEndDate !== endDate),
        reason: typeof data?.reason === "string" ? data.reason : null,
        can_share: !!data?.can_share,
        image: pickPreviewImage(data),
        raw: data,
      };
    } catch (e) {
      return rejectWithValue(
        e?.message || "Failed to validate Prima & Dopo share",
      );
    }
  },
);
